import React, { useEffect, useMemo, useState } from 'react';
import useVisitStore from '../../stores/visitStore';
import { usePermission, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import DoctorQueueCard from './DoctorQueueCard';
import { sortByQueue } from '../../utils/queueSort';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * DoctorDashboard — Internal Queue Dashboard (Redesigned)
 *
 * CHANGES FROM ORIGINAL:
 *   ✔ Shows ALL active doctors even if NO patient registered today
 *   ✔ Uses DoctorQueueCard (reusable, public-display safe)
 *   ✔ Only shows: Token Number, Current Token, Next 5 Waiting, Waiting Count
 *   ✔ Doctor profile info: Name, Photo, Qualification, Department, About
 *   ✗ Does NOT show: Patient Name, Phone, Age, Gender, Symptoms, Notes
 *   ✗ Disabled doctors are EXCLUDED
 *
 * Data sources:
 *   GET /api/organisation/visits/daily  → daily visits
 *   GET /api/organisation/mem?role=DOCTOR → doctors list (with public profile)
 *   Realtime: WS push + 60s reconciliation
 * ═══════════════════════════════════════════════════════════════════════
 */

export default function DoctorDashboard({ onBack }) {
  const canView = usePermission('VIEW_QUEUE');
  const { isScoped, scopeLabel } = useScope();
  const { isDark } = useTheme();
  const {
    dailyVisits, loading, error, fetchDailyVisits, clearError,
    doctors, fetchDoctors,
  } = useVisitStore();

  useEffect(() => { fetchDailyVisits(); }, [fetchDailyVisits]);
  useEffect(() => { if (!doctors || doctors.length === 0) fetchDoctors(); }, [doctors, fetchDoctors]);

  const [selectedDoctor, setSelectedDoctor] = useState('');

  // Build doctor queue cards — ALL active doctors appear even with 0 patients
  const doctorCards = useMemo(() => {
    const activeDoctors = (doctors || []).filter(
      (d) => d.status !== 'DISABLED' && d.status !== 'INACTIVE'
    );

    // Group visits by doctor_member_id
    const visitsByDoctor = {};
    (dailyVisits || []).forEach((v) => {
      const docId = v.doctor_member_id || v.visit_doctor_id;
      if (!visitsByDoctor[docId]) visitsByDoctor[docId] = [];
      visitsByDoctor[docId].push(v);
    });

    return activeDoctors.map((doc) => {
      const docId = doc.member_id || doc.doctor_id;
      const docVisits = visitsByDoctor[docId] || [];

      // Separate by status
      const inProgress = docVisits.filter((v) => v.status === 'IN_PROGRESS');
      const queueVisits = sortByQueue(docVisits.filter((v) => v.status === 'QUEUE'));

      // Build public-safe token data
      const currentToken = inProgress[0]?.queue_number ?? null;
      const currentAppointmentNumber = inProgress[0]?.appointment_number ?? null;
      const currentVisitType = inProgress[0]?.visit_type || null;
      const nextTokens = queueVisits.slice(0, 5).map((v) => {
        const qn = v.queue_number || 0;
        return {
          token: typeof qn === 'number' ? qn : parseFloat(qn) || 0,
          appointmentNumber: v.appointment_number || null,
          visitType: v.visit_type || 'WALK_IN',
          isPriority: v.is_priority || false,
          priorityType: v.priority_type || null,
        };
      });

      return {
        doctor: {
          member_id: docId,
          display_name: doc.display_name || null,
          first_name: doc.first_name || doc.name?.split(' ')[0] || '',
          last_name: doc.last_name || doc.name?.split(' ').slice(1).join(' ') || '',
          profile_picture_url: doc.profile_picture_url || null,
          qualification: doc.qualification || null,
          specialization: doc.specialization || null,
          experience_years: doc.experience_years || null,
          about: doc.about || null,
          consultation_department: doc.consultation_department || null,
          availability: doc.availability || 'available',
        },
        currentToken: currentToken != null ? (typeof currentToken === 'number' ? currentToken : parseFloat(currentToken) || 0) : null,
        currentAppointmentNumber,
        currentVisitType,
        nextTokens,
        waitingCount: queueVisits.length,
      };
    });
  }, [dailyVisits, doctors]);

  const displayedCards = useMemo(() => {
    if (!selectedDoctor) return doctorCards;
    return doctorCards.filter((d) => d.doctor.member_id === selectedDoctor);
  }, [doctorCards, selectedDoctor]);

  const totalWaiting = displayedCards.reduce((s, d) => s + d.waitingCount, 0);
  const totalServing = displayedCards.filter((d) => d.currentToken != null).length;
  const totalDoctors = displayedCards.length;

  if (!canView) return null;

  return (
    <div style={{
      borderRadius: '1rem',
      overflow: 'hidden',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
      background: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
    }}>
      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
        padding: '0.875rem 1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.125rem' }}>📋</span>
            <h2 style={{ margin: 0, color: '#fff', fontWeight: 600, fontSize: '1rem' }}>
              {isScoped ? `${scopeLabel} Queue` : 'Live Queue Status'}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={() => fetchDailyVisits()}
              disabled={loading.daily}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '0.8125rem' }}
            >
              ↻ Refresh
            </button>
            {onBack && (
              <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.875rem' }}>✕</button>
            )}
          </div>
        </div>
      </div>

      {/* ── Doctor Filter ── */}
      {doctorCards.length > 1 && (
        <div style={{
          padding: '0.5rem 1rem',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: isDark ? '#9ca3af' : '#6b7280', whiteSpace: 'nowrap' }}>
            Filter:
          </span>
          <select
            value={selectedDoctor}
            onChange={(e) => setSelectedDoctor(e.target.value)}
            style={{
              flex: 1,
              padding: '0.3125rem 0.5rem',
              borderRadius: '0.375rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
              color: isDark ? '#f5f5f7' : '#111827',
              fontSize: '0.75rem',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">All Doctors</option>
            {doctorCards.map((d) => {
              const avail = d.doctor.availability;
              const suffix = avail === 'on_hold' ? ' ⏸' : avail === 'closed_today' ? ' 🚫' : '';
              const name = d.doctor.display_name || `${d.doctor.first_name} ${d.doctor.last_name}`.trim();
              return (
                <option key={d.doctor.member_id} value={d.doctor.member_id}>
                  Dr. {name}{suffix}
                </option>
              );
            })}
          </select>
          {selectedDoctor && (
            <button
              onClick={() => setSelectedDoctor('')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280',
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {/* ── Summary Strip ── */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
      }}>
        <SummaryPill label="Waiting" value={totalWaiting} color="#3b82f6" isDark={isDark} />
        <SummaryPill label="Consulting" value={totalServing} color="#0d9488" isDark={isDark} border />
        <SummaryPill label="Doctors" value={totalDoctors} color="#0d9488" isDark={isDark} border />
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          padding: '0.5rem 1.25rem',
          background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
          borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.15)' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '0.75rem', color: isDark ? '#fca5a5' : '#991b1b' }}>⚠️ {error}</span>
          <button onClick={clearError} style={{ background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444', cursor: 'pointer', fontSize: '0.625rem' }}>Dismiss</button>
        </div>
      )}

      {/* ── Loading ── */}
      {loading.daily && displayedCards.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center', fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>Loading queue data...</div>
      )}

      {/* ── Empty: no active doctors ── */}
      {!loading.daily && displayedCards.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <span style={{ fontSize: '2.5rem' }}>🏥</span>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', color: isDark ? '#e8e9ed' : '#374151' }}>No active doctors today</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>Doctor queues will appear once doctors are active</p>
        </div>
      )}

      {/* ── Doctor Queue Cards ── */}
      <div style={{
        padding: '0.75rem 1rem',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '0.75rem',
      }}>
        {displayedCards.map((card) => (
          <DoctorQueueCard
            key={card.doctor.member_id}
            doctor={card.doctor}
            currentToken={card.currentToken}
            currentAppointmentNumber={card.currentAppointmentNumber}
            currentVisitType={card.currentVisitType}
            nextTokens={card.nextTokens}
            waitingCount={card.waitingCount}
            isDark={isDark}
          />
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// SummaryPill — top-level stat cell
// ═══════════════════════════════════════════════════════════════════════

function SummaryPill({ label, value, color, isDark, border }) {
  return (
    <div style={{
      flex: 1,
      padding: '0.625rem 0.5rem',
      textAlign: 'center',
      borderLeft: border ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none',
    }}>
      <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color }}>{value}</p>
      <p style={{
        margin: '0.0625rem 0 0', fontSize: '0.5rem', fontWeight: 600,
        color: isDark ? '#6b7280' : '#9ca3af',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>{label}</p>
    </div>
  );
}
